/*
 * Client-side tachograph file recognition.
 *
 * Reads a tachograph file's bytes to determine whether it is a driver card or a vehicle
 * unit, and extracts a short description to show before upload: a driver card's holder
 * name and last-download date, or a vehicle unit's registration and current date. This
 * only drives the description shown in the upload list, so every extraction is
 * best-effort and falls back to a hyphen when the bytes don't line up.
 *
 * All multi-byte integers in tachograph files are big-endian (the first byte is the
 * most significant).
 */

export const TachoFileKind = Object.freeze({
    CARD: "card",
    VU: "vu"
});

// The leading byte that tags vehicle unit files, the Gen1 driver-card prefix, and the
// day-boundary marker inside Gen2 vehicle unit files.
const FILE_TAG = 0x76;
// The second byte of the Gen1 driver-card prefix (0x76 0x06), the prefix's length, and
// the signature that marks driver-card content (with or without that prefix).
const GEN1_CARD_PREFIX = 0x06;
const GEN1_CARD_PREFIX_LENGTH = 2;
const DRIVER_CARD_CONTENT_SIGNATURE = [0x00, 0x02];

// A vehicle unit's generation is identified by the range of its second byte.
const VU_GEN1_GENERATION_MIN = 0x01;
const VU_GEN1_GENERATION_MAX = 0x05;
const VU_GEN2_GENERATION_MIN = 0x21;
const VU_GEN2_GENERATION_MAX = 0x25;
const VU_GEN2_V2_GENERATION_MIN = 0x31;
const VU_GEN2_V2_GENERATION_MAX = 0x35;

// A Gen2 V2 download is prefixed with a 4-byte "VU Download Interface Version" header:
// 0x76 0x00 followed by two version bytes, sitting before the first TREP marker. Its
// 0x00 second byte distinguishes it from a file that begins straight at a 0x76 TREP marker.
const VU_GEN2_V2_DOWNLOAD_INTERFACE_MARKER = 0x00;
const VU_GEN2_V2_HEADER_LENGTH = 4;

// Fixed-length string fields are right-padded with 0xFF.
const STRING_PADDING_BYTE = 0xff;

// Section identifiers within a driver card file.
const CARD_IDENTIFICATION_SECTION = 0x0520;
const CARD_DOWNLOAD_SECTION = 0x050e;

// Within the identification section the holder name is two fixed-length fields. Each is
// one code-page byte followed by a 35-byte string: the surname starts at offset 65 and
// the first names at offset 101 (the preceding nation, card number, issuing authority
// and three date fields take up the first 65 bytes).
const CARD_SURNAME_OFFSET = 65;
const CARD_FIRST_NAMES_OFFSET = 101;
const NAME_CODE_PAGE_BYTES = 1;
const NAME_STRING_LENGTH = 35;

// Section identifiers within a Gen2 vehicle unit file. Version 1 holds the registration
// in 0x0B as a plain registration number (code-page byte then the string); Version 2
// holds it in 0x24 with a leading nation byte before that registration number.
const VU_GEN2_REGISTRATION_SECTION = 0x0b;
const VU_GEN2_VER2_REGISTRATION_SECTION = 0x24;
const VU_GEN2_CURRENT_DATE_SECTION = 0x03;

// A Gen1 vehicle unit is read in order starting from its overview block (0x76 0x01).
// After the 2-byte header come two 194-byte certificates, a 17-byte vehicle id number,
// a 1-byte registration nation and a 1-byte code page before the 13-byte registration,
// then the 4-byte current date — so the registration sits 409 bytes after the marker
// and the date 422 bytes after it.
const VU_GEN1_OVERVIEW_MARKER = [FILE_TAG, 0x01];
const VU_GEN1_REGISTRATION_FROM_MARKER = 409;
const VU_GEN1_DATE_FROM_MARKER = 422;

const VU_REGISTRATION_NATION_BYTES = 1;
const VU_REGISTRATION_CODE_PAGE_BYTES = 1;
const VU_REGISTRATION_STRING_LENGTH = 13;

// A driver card section header is [identifier:2][section type:1][length:2]; a Gen2
// vehicle unit block header is [identifier:1][record size:2][entry count:2].
const TLV_HEADER_LENGTH = 5;
const TLV_LENGTH_OFFSET = 3;
const VU_GEN2_HEADER_LENGTH = 5;
const VU_GEN2_ENTRIES_OFFSET = 3;
const TIME_REAL_LENGTH = 4;
const MILLISECONDS_PER_SECOND = 1000;

function readUint16BigEndian(bytes, offset) {
    return new DataView(bytes.buffer, bytes.byteOffset + offset).getUint16(0, false);
}

function readUint32BigEndian(bytes, offset) {
    return new DataView(bytes.buffer, bytes.byteOffset + offset).getUint32(0, false);
}

// Reads a fixed-length string field: stop at the first null byte, drop 0xFF padding,
// decode as ASCII and trim.
function readTachoString(bytes, offset, length) {
    if (offset + length > bytes.length) {
        return "";
    }

    let result = "";
    for (let i = 0; i < length; i++) {
        const byte = bytes[offset + i];
        if (byte === 0x00) {
            break;
        }
        if (byte === STRING_PADDING_BYTE) {
            continue;
        }
        result += String.fromCharCode(byte);
    }

    return result.trim();
}

// Reads a date field: a big-endian count of seconds since the Unix epoch. A value at or
// before the epoch means "no date".
function readTimeReal(bytes, offset) {
    if (offset + TIME_REAL_LENGTH > bytes.length) {
        return null;
    }

    const seconds = readUint32BigEndian(bytes, offset);
    if (seconds <= 0) {
        return null;
    }

    return new Date(seconds * MILLISECONDS_PER_SECOND);
}

// Compares the bytes at an offset against a fixed sequence.
function matchesSequenceAt(bytes, offset, sequence) {
    for (let i = 0; i < sequence.length; i++) {
        if (bytes[offset + i] !== sequence[i]) {
            return false;
        }
    }
    return true;
}

// Detects whether the bytes are a driver card from their signature. A Gen1 card wraps the
// signature in a 0x76 0x06 prefix; otherwise the signature is at the start.
function isDriverCardSignature(bytes) {
    if (bytes[0] === FILE_TAG && bytes[1] === GEN1_CARD_PREFIX) {
        return matchesSequenceAt(bytes, GEN1_CARD_PREFIX_LENGTH, DRIVER_CARD_CONTENT_SIGNATURE);
    }

    return matchesSequenceAt(bytes, 0, DRIVER_CARD_CONTENT_SIGNATURE);
}

// Detects a vehicle unit and its generation from the leading bytes. Gen2 and Gen2 V2
// are walked the same way, so both return "gen2".
function getVehicleUnitGeneration(bytes) {
    if (bytes.length < 2 || bytes[0] !== FILE_TAG) {
        return null;
    }

    const second = bytes[1];

    if (second >= VU_GEN1_GENERATION_MIN && second <= VU_GEN1_GENERATION_MAX) {
        return "gen1";
    }

    if (second >= VU_GEN2_GENERATION_MIN && second <= VU_GEN2_GENERATION_MAX) {
        return "gen2";
    }

    if (second === 0x00 || (second >= VU_GEN2_V2_GENERATION_MIN && second <= VU_GEN2_V2_GENERATION_MAX)) {
        return "gen2";
    }

    return null;
}

// Walks the standard TLV layout used by driver card files: an optional 0x76 0x06
// prefix, then repeated [identifier:2][sectionType:1][length:2][contents:length].
function readCardSections(bytes) {
    const sections = [];
    let pointer = 0;

    if (bytes.length > 1 && bytes[0] === FILE_TAG && bytes[1] === GEN1_CARD_PREFIX) {
        pointer = 2;
    }

    while (pointer + TLV_HEADER_LENGTH <= bytes.length) {
        const identifier = readUint16BigEndian(bytes, pointer);
        const length = readUint16BigEndian(bytes, pointer + TLV_LENGTH_OFFSET);
        const contentStart = pointer + TLV_HEADER_LENGTH;

        if (contentStart + length > bytes.length) {
            break;
        }

        sections.push({
            identifier: identifier,
            contents: bytes.subarray(contentStart, contentStart + length)
        });

        pointer = contentStart + length;
    }

    return sections;
}

function findSection(sections, identifier) {
    return sections.find((section) => section.identifier === identifier) ?? null;
}

function readDriverCard(bytes) {
    const sections = readCardSections(bytes);

    let name = null;
    const identification = findSection(sections, CARD_IDENTIFICATION_SECTION);
    if (identification != null) {
        const surname = readTachoString(identification.contents, CARD_SURNAME_OFFSET + NAME_CODE_PAGE_BYTES, NAME_STRING_LENGTH);
        const firstNames = readTachoString(identification.contents, CARD_FIRST_NAMES_OFFSET + NAME_CODE_PAGE_BYTES, NAME_STRING_LENGTH);
        const combined = `${firstNames} ${surname}`.trim();
        if (combined.length > 0) {
            name = combined;
        }
    }

    let date = null;
    const cardDownload = findSection(sections, CARD_DOWNLOAD_SECTION);
    if (cardDownload != null) {
        date = readTimeReal(cardDownload.contents, 0);
    }

    return { kind: TachoFileKind.CARD, name: name, registration: null, date: date };
}

function indexOfSequence(bytes, sequence) {
    const limit = bytes.length - sequence.length;
    for (let i = 0; i <= limit; i++) {
        if (matchesSequenceAt(bytes, i, sequence)) {
            return i;
        }
    }

    return -1;
}

function readVehicleUnitGen1(bytes) {
    let registration = null;
    let date = null;

    const markerIndex = indexOfSequence(bytes, VU_GEN1_OVERVIEW_MARKER);
    if (markerIndex >= 0) {
        const reg = readTachoString(bytes, markerIndex + VU_GEN1_REGISTRATION_FROM_MARKER, VU_REGISTRATION_STRING_LENGTH);
        if (reg.length > 0) {
            registration = reg;
        }
        date = readTimeReal(bytes, markerIndex + VU_GEN1_DATE_FROM_MARKER);
    }

    return { kind: TachoFileKind.VU, name: null, registration: registration, date: date };
}

// Walks the Gen2 vehicle unit layout: a flat sequence of blocks, each
// [id:1][recordSize:2][entries:2][recordSize * entries bytes], with 0x76 day-boundary
// markers that consume two bytes and are skipped. We only need the first record of two
// sections, so we stop once both are found.
function readVehicleUnitGen2(bytes) {
    let registration = null;
    let date = null;

    // Gen2 V2 downloads prepend the 4-byte VU Download Interface Version header before the
    // first TREP marker; skip it so the walk starts on a real marker. Files that begin
    // straight at a 0x76 TREP marker (no header) start at offset 2.
    // (Ref. Reg. (EU) 2016/799 Annex 1C Appendix 7.)
    let pointer = bytes[1] === VU_GEN2_V2_DOWNLOAD_INTERFACE_MARKER ? VU_GEN2_V2_HEADER_LENGTH : 2;
    while (pointer < bytes.length) {
        const id = bytes[pointer];

        if (id === FILE_TAG) {
            pointer += 2;
            continue;
        }

        if (pointer + VU_GEN2_HEADER_LENGTH > bytes.length) {
            break;
        }

        const recordSize = readUint16BigEndian(bytes, pointer + 1);
        const entries = readUint16BigEndian(bytes, pointer + VU_GEN2_ENTRIES_OFFSET);
        const contentStart = pointer + VU_GEN2_HEADER_LENGTH;
        const arraySize = recordSize * entries;

        if (recordSize <= 0 || contentStart + arraySize > bytes.length) {
            break;
        }

        const isRegistrationSection = id === VU_GEN2_REGISTRATION_SECTION || id === VU_GEN2_VER2_REGISTRATION_SECTION;
        if (isRegistrationSection && registration == null && entries > 0) {
            // Version 2 (0x24) prefixes the number with a nation byte; Version 1 (0x0B)
            // does not. Both then have the code-page byte before the registration string.
            const nationBytes = id === VU_GEN2_VER2_REGISTRATION_SECTION ? VU_REGISTRATION_NATION_BYTES : 0;
            const reg = readTachoString(bytes, contentStart + nationBytes + VU_REGISTRATION_CODE_PAGE_BYTES, VU_REGISTRATION_STRING_LENGTH);
            if (reg.length > 0) {
                registration = reg;
            }
        } else if (id === VU_GEN2_CURRENT_DATE_SECTION && date == null && entries > 0) {
            date = readTimeReal(bytes, contentStart);
        }

        if (registration != null && date != null) {
            break;
        }

        pointer = contentStart + arraySize;
    }

    return { kind: TachoFileKind.VU, name: null, registration: registration, date: date };
}

function emptyRecognition(kind) {
    return { kind: kind, name: null, registration: null, date: null };
}

/**
 * Recognises a tachograph file from its bytes.
 *
 * @param {ArrayBuffer} arrayBuffer The raw file bytes.
 * @returns {{kind: ('card'|'vu'|null), name: (string|null), registration: (string|null), date: (Date|null)}}
 *          kind is null when the file is not a recognised tachograph file.
 */
export function recogniseTachoFile(arrayBuffer) {
    const bytes = new Uint8Array(arrayBuffer);

    if (isDriverCardSignature(bytes)) {
        return safeExtract(() => readDriverCard(bytes), TachoFileKind.CARD);
    }

    const vuGeneration = getVehicleUnitGeneration(bytes);
    if (vuGeneration === "gen1") {
        return safeExtract(() => readVehicleUnitGen1(bytes), TachoFileKind.VU);
    }
    if (vuGeneration === "gen2") {
        return safeExtract(() => readVehicleUnitGen2(bytes), TachoFileKind.VU);
    }

    return { kind: null, name: null, registration: null, date: null };
}

// Description extraction is cosmetic, so any unexpected layout falls back to the kind
// with no details rather than failing the whole recognition.
function safeExtract(extractor, kind) {
    try {
        return extractor();
    } catch {
        return emptyRecognition(kind);
    }
}
